/**
 * Side Menu Base
 * @description 사이드 메뉴 공통 로직
 */

import { $, getHeaderAttributes } from '../../../core';
import type { SideMenuElements } from './types';

/**
 * 사이드 메뉴 요소 선택
 */
export const getSideMenuElements = (): SideMenuElements | null => {
  const root = $<HTMLElement>('.js-side-menu');
  if (!root) {
    return null;
  }

  const list = $<HTMLElement>('.nde-c-side-menu__list', root);
  const leftArrow = $<HTMLElement>('.nde-c-side-menu__arrow--left', root);
  const rightArrow = $<HTMLElement>('.nde-c-side-menu__arrow--right', root);

  if (!list || !leftArrow || !rightArrow) {
    return null;
  }

  return { root, list, leftArrow, rightArrow };
};

/**
 * 화살표 네비게이션 설정
 */
export const setupArrowNavigation = (
  elements: SideMenuElements,
  updateArrows: () => void,
  updatePadding: () => void,
  scroll: (direction: 'left' | 'right') => void
): void => {
  const { list, leftArrow, rightArrow } = elements;

  // 인라인 display:none 제거 → CSS 클래스로 제어 전환
  leftArrow.style.removeProperty('display');
  rightArrow.style.removeProperty('display');

  const leftButton = $<HTMLButtonElement>('button', leftArrow);
  const rightButton = $<HTMLButtonElement>('button', rightArrow);

  if (leftButton) {
    leftButton.onclick = () => scroll('left');
  }
  if (rightButton) {
    rightButton.onclick = () => scroll('right');
  }

  // 스크롤: 화살표만 토글 / 리사이즈: 패딩도 재계산
  list.addEventListener('scroll', updateArrows);
  window.addEventListener('resize', () => {
    updatePadding();
    updateArrows();
  });

  // 초기 체크: 렌더링 완료 후 레이아웃 기반으로 판단
  requestAnimationFrame(() => {
    updatePadding();
    updateArrows();
  });
};

/**
 * 메뉴 타입 체크
 */
export type SideMenuType = 'dropdown' | 'mega-menu' | 'drawer' | null;

export const getSideMenuType = (): SideMenuType => {
  const attrs = getHeaderAttributes();

  if (attrs.upperMenuType === 'category' && attrs.categoryType === 'sub') {
    return 'dropdown';
  }

  if (attrs.upperMenuType === 'category' && attrs.categoryType === 'all') {
    return 'mega-menu';
  }

  if (attrs.upperMenuType === 'hamburger') {
    return 'drawer';
  }

  return null;
};

/**
 * 스크립트 실행 조건 체크
 */
export const shouldRunMenuScript = (type: SideMenuType): boolean => {
  return getSideMenuType() === type;
};
